import "./StatsRow.scss"

import React, { useEffect, useState } from "react"
import { PostStatus } from "@fider/models"
import { i18n } from "@lingui/core"

// Tùy biến KHCN: dải chỉ số theo bản thiết kế. CHỈ dùng số thật từ countPerStatus có sẵn
// (không có API đếm thành viên/hợp tác nên không bịa số). Số đếm chạy lên khi hiển thị.

interface StatsRowProps {
  countPerStatus: { [key: string]: number }
}

const GLYPH = {
  bulb: "M9.7 18h4.6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.4 1.3 2.2h4.6c.2-.8.6-1.6 1.3-2.2A6 6 0 0 0 12 3z",
  chat: "M8 10h8M8 14h5M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z",
  calendar: "M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM8 13h3v3H8z",
  rocket: "M14 4c3 0 6 3 6 6-2 3-5 6-9 8l-5-5c2-4 5-7 8-9zM9 15l-4 5M15.5 8.5h.01M5 14l-2 1 3 3 1-2",
  trophy: "M8 4h8v5a4 4 0 0 1-8 0V4zM8 6H5a2 2 0 0 0 2 4h1M16 6h3a2 2 0 0 1-2 4h-1M12 13v4M9 20h6",
}

// 1256 -> "1.256" theo cách viết số của tiếng Việt
const formatNumber = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".")

// Đếm chạy từ 0 lên giá trị thật trong ~0,9s; tôn trọng prefers-reduced-motion
const useCountUp = (target: number): number => {
  const [value, setValue] = useState(target)

  useEffect(() => {
    if (target === 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target)
      return
    }
    const start = performance.now()
    const duration = 900
    let frame = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(Math.round(target * eased))
      if (t < 1) {
        frame = requestAnimationFrame(tick)
      }
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target])

  return value
}

const StatCard = ({ tone, value, label, d }: { tone: string; value: number; label: string; d: string }) => {
  const shown = useCountUp(value)
  return (
    <div className={`kh-stats__card kh-stats__card--${tone}`}>
      <span className="kh-stats__icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={d} />
        </svg>
      </span>
      <span className="kh-stats__value">{formatNumber(shown)}</span>
      <span className="kh-stats__label">{label}</span>
    </div>
  )
}

export const StatsRow = ({ countPerStatus }: StatsRowProps) => {
  const count = (status: string) => countPerStatus[status] || 0
  const total = Object.keys(countPerStatus)
    .filter((status) => status !== PostStatus.Deleted.value)
    .reduce((sum, status) => sum + countPerStatus[status], 0)

  const items = [
    { tone: "blue", value: total, label: i18n._({ id: "khcn.stats.total", message: "Ideas posted" }), d: GLYPH.bulb },
    { tone: "green", value: count(PostStatus.Open.value), label: i18n._({ id: "khcn.stats.open", message: "In discussion" }), d: GLYPH.chat },
    { tone: "purple", value: count(PostStatus.Planned.value), label: i18n._({ id: "khcn.stats.planned", message: "Planned" }), d: GLYPH.calendar },
    { tone: "orange", value: count(PostStatus.Started.value), label: i18n._({ id: "khcn.stats.started", message: "In progress" }), d: GLYPH.rocket },
    { tone: "teal", value: count(PostStatus.Completed.value), label: i18n._({ id: "khcn.stats.completed", message: "Completed" }), d: GLYPH.trophy },
  ]

  return (
    <section className="kh-stats" aria-label={i18n._({ id: "khcn.stats.label", message: "Forum statistics" })}>
      {items.map((item) => (
        <StatCard key={item.tone} {...item} />
      ))}
    </section>
  )
}
